import { TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import FormMonitoring from 'components/FormMonitoring';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppColors } from 'resources/AppColors';
import SingleChoiceDropdown from 'components/Dropdowns/SingleChoiceDropdown';
import {
  initializeAppsOptions,
  initializeBusinessPackageOptions,
} from 'redux/reducers/dataAppAndPackageReducer';
import { centersTexts, CommonTexts, ConfigFormsLabels } from 'locale/es';
import { toast } from 'redux/reducers/notificationReducer';
import StudyService from 'services/StudyService';
import Loader from 'components/Loader/Loader';
import ConfigCenterOptions from './ConfigCenterOptions';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { zodInputNumberPipe } from 'types';
import { isEmptyObj } from 'utils/HelperFunctions';

const useStyles = makeStyles((theme) => ({
  textFieldLabel: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: AppColors.PRIMARY,
      },
    },
    '& .MuiInputBase-root': {
      color: AppColors.PRIMARY,
    },
    '& .MuiInputLabel-root': {
      color: AppColors.PRIMARY,
    },
    color: AppColors.PRIMARY,
    backgroundColor: AppColors.BOX_FILL,
  },
}));

const MaxParticipantsType = zodInputNumberPipe(z.number().int().gt(0).max(999999).optional());

const HealthDataOptions = ["steps", "calories", "HR", "weight", "distance"]

const CenterComponentType = z.object({
  healthDataService: z.boolean(),
  noteService: z.boolean(),
  messageService: z.boolean(),
  contentService: z.boolean(),
  formService: z.boolean(),
})

const CenterFormSchema = z.object({
  name: z.string().min(2, centersTexts.formInputErrors.name),
  app: z.string().optional(),
  businessPackage: z.string().optional(),
  organization: z.string().optional(),
  studyId: z.string().optional(),
  centerComponents: CenterComponentType,
  healthDataOptions: z.array(z.enum(HealthDataOptions)),
  maxParticipants: MaxParticipantsType,
  enabledSelfRegistration: z.boolean(),
  uuid: z.string().nullable()
})
  .transform(data => {
    const transformedData = {
      ...data,
      app: { id: data.app },
      businessPackage: { id: data.businessPackage },
      organization: { id: data.organization },
      status: "ACTIVE",
      deletionDate: null,
      configuration: {
        ...data.centerComponents,
        healthDataTypes: data.healthDataOptions.join(","),
        enabledSelfRegistration: data.enabledSelfRegistration,
        maxParticipants: data.maxParticipants,
        registrationLink: null,
        id: null,
      }
    }

    return transformedData;
  })

function CenterForm({ handleShow, centers, selectedCenter, onSubmit }) {
  const [studies, setStudies] = useState(undefined);
  const [formError, setFormError] = useState("");

  const nameInputRef = useRef()

  const isEdit = Boolean(selectedCenter);

  const { data: organizations, loading, error } = useSelector(
    ({ organizations }) => organizations
  );
console.log('organizations', organizations);

  const organizationsOptions = useMemo(()=> {
    return organizations?.map(org => ({
      value: org.id,
      name: org.name
    })) || [];
  }, [organizations])

  const { handleSubmit, control, reset, getValues } = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: zodResolver(CenterFormSchema),
    resetOptions: {
      keepDefaultValues: true,
    },
    defaultValues: {
      uuid: selectedCenter?.uuid || null,
      name: selectedCenter?.name || "",
      app: selectedCenter?.app?.id || "",
      businessPackage: selectedCenter?.businessPackage?.id || "",
      organization: selectedCenter?.organization?.id || "",
      studyId: selectedCenter?.studyId || "",
      centerComponents: {
        healthDataService: selectedCenter?.configuration?.healthDataService ?? true,
        noteService: selectedCenter?.configuration?.noteService ?? true,
        messageService: selectedCenter?.configuration?.messageService ?? true,
        contentService: selectedCenter?.configuration?.contentService ?? true,
        formService: selectedCenter?.configuration?.formService ?? true,
      },
      healthDataOptions: selectedCenter?.configuration?.healthDataTypes?.split(",") || HealthDataOptions,
      maxParticipants: selectedCenter?.configuration?.maxParticipants || "",
      enabledSelfRegistration: isEdit ? !isEmptyObj(selectedCenter?.configuration) ? selectedCenter?.configuration?.enabledSelfRegistration : false : true,
    },
  });
  
  const { appsOptions, businessPackageOptions } = useSelector(
    ({ optionsAppAndBusiness }) => optionsAppAndBusiness
  );

  const classes = useStyles();

  const dispatch = useDispatch();

  useEffect(() => {
    if (!appsOptions) {
      dispatch(initializeAppsOptions());
    }
  }, [appsOptions, dispatch]);

  useEffect(() => {
    if (!businessPackageOptions) {
      dispatch(initializeBusinessPackageOptions());
    }
  }, [businessPackageOptions, dispatch]);

  useEffect(() => {
    if (!studies) {
      getStudies();
    }

    async function getStudies() {
      try {
        // TODO: Move to reducer
        const { data } = await StudyService.getAll();

        if (!data.data.length)
          throw new Error('No studies found for this user');

        const userStudies = data.data.map((study) => ({
          value: study.id.toString(),
          name: study.translations[0]?.study_title,
        }));

        setStudies(userStudies);
      } catch (e) {
        dispatch(toast(e.message, 'error', 10));
        handleShow();
      }
    }
  }, [dispatch, handleShow, studies]);

  async function handleOnConfirmSubmit(data) {
    try {
      if (!!centers) {
        const newCenterName = getValues("name")
        const centerUUIDExists = centers.some((center) => {
          if (centers.length === 0) return false;

          return center.uuid === newCenterName;
        });

        if (centerUUIDExists) {
          nameInputRef.current.focus()
          throw new Error(centersTexts.formInputErrors.name_error);
        } else if (formError && !formError.response) {
          setFormError("")
        }
      }

      const organizationObject = organizations.find((org) => org.id === data.organization.id)

      await dispatch(onSubmit({ ...data, id: selectedCenter?.id, organization: organizationObject }));
      Boolean(handleShow) && handleShow();
    } catch (error) {
      console.error(error)
      setFormError(error);
    }
  }

  if (!studies) {
    return <Loader message={centersTexts.LOADING} />
  }

  return (
    <FormMonitoring
      submitForm={handleSubmit(handleOnConfirmSubmit)}
      resetFormInputs={reset}
      isEdit={Boolean(selectedCenter)}
    >
      {selectedCenter?.uuid && (
        <TextField
          disabled
          value={selectedCenter?.uuid}
          type='text'
          label='UUID'
          margin='normal'
          variant='outlined'
          className={classes.textFieldLabel}
        />
      )}
      <Controller
        control={control}
        name='name'
        render={({
          field,
          fieldState: { error },
        }) => (
          <TextField
            {...field}
            type='text'
            inputProps={{
              ref: nameInputRef
            }}
            InputLabelProps={{
              required: true
            }}
            label={CommonTexts.NAME}
            margin='normal'
            variant='outlined'
            autoFocus
            className={classes.textFieldLabel}
            error={Boolean(error) || Boolean(formError && !formError.response)}
            helperText={error?.message || (formError && !formError.response) ? error?.message || formError.message : ""}
          />
        )}
      />

      <Controller
        control={control}
        name='studyId'
        render={({
          field: { onChange, onBlur, value },
        }) => (
          <SingleChoiceDropdown
            label={ConfigFormsLabels.STUDY_NAME}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            items={studies}
          />
        )}
      />

      <Controller
        control={control}
        name='app'
        render={({
          field: { onChange, onBlur, value },
        }) => (
          <SingleChoiceDropdown
            label={'App'}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            items={appsOptions}
          />
        )}
      />
      <Controller
        control={control}
        name='businessPackage'
        render={({
          field: { onChange, onBlur, value },
        }) => (
          <SingleChoiceDropdown
            label={centersTexts.PACKAGE}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            items={businessPackageOptions}
          />
        )}
      />
       <Controller
        control={control}
        name='organization'
        render={({
          field: { onChange, onBlur, value },
        }) => (
          <SingleChoiceDropdown
            label={centersTexts.ORGANIZATION}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            items={organizationsOptions} />
        )}
      />
      <ConfigCenterOptions
        control={control}
        healthDataOptions={HealthDataOptions}
        selectedCenter={selectedCenter}
      />
      <Typography mt={1} variant='body' sx={{ color: "red" }}>
        {formError.response && centersTexts.CREATION_ERROR}
      </Typography>
    </FormMonitoring>
  );
}

export default CenterForm;
